/** @jsxImportSource theme-ui */
import {
  createDiseaseGroup,
  deleteUserGroup,
  editDiseaseGroup,
  fetchDiseaseGroups,
} from 'admin/admin.saga';
import * as AdminSelectors from 'admin/admin.selectors';
import { FormikProps } from 'formik';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'semantic-ui-react';
import * as Yup from 'yup';

import * as dto from 'client/dto';
import { nameof } from 'utils/typeHelpers';

import { Typography } from 'components/_common/Typography';
import BdFormLabel from 'components/SidebarView/SettingsView/_common/BdFormLabel';
import { FormikSemanticInput } from 'components/SidebarView/SettingsView/FormikControls/FormikSemanticInput';
import { FormikSemanticMultiselect } from 'components/SidebarView/SettingsView/FormikControls/FormikSemanticMultiselect';

import { fetchAllAvailableDiseases } from 'diseases/diseases.saga';
import diseasesSelectors from 'diseases/diseases.selectors';

import { SimpleCrudTable } from '../_common/SimpleCrudTable';

type DiseaseGroupFM = {
  id: number;
  name: string;
  diseases: number[];
};

const DiseaseGroupFMValidationSchema = Yup.object().shape<Partial<DiseaseGroupFM>>({
  name: Yup.string()
    .required('name is required')
    .max(256, 'name cannot be longer than 256 characters'),
});

// =====================================================================================================================================

const RenderFormikDiseaseGroupCreateNew = ({
  initialValues,
  touched,
  errors,
}: FormikProps<DiseaseGroupFM>) => {
  return (
    <>
      <BdFormLabel>Name</BdFormLabel>
      <FormikSemanticInput
        placeholder="Name"
        name={nameof<DiseaseGroupFM>('name')}
        error={touched.name && !!errors.name}
      />
    </>
  );
};

// =====================================================================================================================================

const RenderFormikDiseaseGroup = ({
  initialValues,
  touched,
  errors,
}: FormikProps<DiseaseGroupFM>) => {
  const dispatch = useDispatch();

  const isLoadingDiseases = useSelector(diseasesSelectors.isDiseasesLoading);
  const diseases = useSelector(diseasesSelectors.allAvailableDiseasesAsDropdownItem);

  useEffect(() => {
    dispatch(fetchAllAvailableDiseases());
  }, []);

  return (
    <>
      <BdFormLabel>Name</BdFormLabel>
      <FormikSemanticInput
        placeholder="Name"
        name={nameof<DiseaseGroupFM>('name')}
        error={touched.name && !!errors.name}
      />
      <BdFormLabel>Diseases</BdFormLabel>
      <FormikSemanticMultiselect
        placeholder="Diseases"
        name={nameof<DiseaseGroupFM>('diseases')}
        options={diseases}
        loading={isLoadingDiseases}
      />
    </>
  );
};

// =====================================================================================================================================

const DiseaseGroup: React.FC = () => {
  const dispatch = useDispatch();

  const isLoading = useSelector(AdminSelectors.isAdminLoadingSelector);
  const dataList = useSelector(AdminSelectors.diseaseGroupsSelector);

  useEffect(() => {
    dispatch(fetchDiseaseGroups());
  }, []);

  const handleItemCreate = useCallback(({ name }: DiseaseGroupFM) => {
    dispatch(createDiseaseGroup({ name }));
  }, []);

  const handleItemEdit = useCallback(({ id, name, diseases }: DiseaseGroupFM) => {
    dispatch(editDiseaseGroup({ id, data: { name, diseaseIds: diseases } }));
  }, []);

  const handleItemDelete = useCallback(({ id }: dto.CustomDiseaseGroup) => {
    dispatch(deleteUserGroup(id));
  }, []);

  return (
    <div>
      <h1>DiseaseGroup Page</h1>

      <SimpleCrudTable<dto.CustomDiseaseGroup, DiseaseGroupFM>
        loading={isLoading}
        list={dataList}
        entityType="Disease Group"
        columns={[
          { name: 'Id', getter: x => x.id },
          { name: 'Name', getter: x => x.name },
        ]}
        keyGetter={x => `${x.id}`}
        createNew={{
          title: 'Create New Disease Group',
          validationSchema: DiseaseGroupFMValidationSchema,
          renderForm: RenderFormikDiseaseGroupCreateNew,
          onSubmitForm: handleItemCreate,
        }}
        actions={[
          {
            type: 'custom',
            actionText: 'Edit',
            custom: {
              validationSchema: DiseaseGroupFMValidationSchema,
              renderTitle: item => `Edit disease group "${item.name}"`,
              renderForm: RenderFormikDiseaseGroup,
              onSubmitForm: handleItemEdit,
              mapToFormData: (item: dto.CustomDiseaseGroup) =>
                ({
                  id: item.id,
                  name: item.name,
                  diseases: item.diseaseIds,
                } as DiseaseGroupFM),
            },
          },
          {
            type: 'confirm',
            actionText: 'Delete',
            confirm: {
              render: (item: dto.CustomDiseaseGroup) => (
                <Modal.Content>
                  <Typography variant="h6" color="stone90">
                    Are you sure you want to delete the Disease Group &quot;{item.name}&quot;?
                  </Typography>
                  <Typography variant="subtitle2" color="clay100">
                    Deleting this Disease Group will unassign all diseases from this disease group.
                    It will not delete the diseases.
                  </Typography>
                </Modal.Content>
              ),
              onSubmit: handleItemDelete,
            },
          },
        ]}
      />
    </div>
  );
};

export default DiseaseGroup;
